import React, { useCallback, useMemo } from 'react'
import { Button, Col, Row } from 'reactstrap'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { getAllResults } from '../../../../redux/slices'
import {
  RESULTS_CHUNK_SIZE,
  setPageIndex,
  setPendingPageIndex
} from '../../../../redux/slices/resultsSlice'
import { AppDispatch } from '../../../../services/lib'
import '../ResultsGrid.css'

const ResultsPagination: React.FC = () => {
  const dispatch: AppDispatch = useDispatch()
  const {
    total,
    pageSize,
    pageIndex,
    chunkSize,
    offset,
    pendingPageIndex,
    currentQuery,
    loading
  } = useAppSelector((state) => state.results)

  const hasQuery = Boolean(currentQuery)

  const totalPages = useMemo(() => {
    if (!hasQuery || !pageSize || total === 0) {
      return 0
    }

    return Math.ceil(total / pageSize)
  }, [hasQuery, pageSize, total])

  const currentPage = totalPages === 0 ? 0 : pageIndex + 1
  const isBusy = loading || pendingPageIndex !== null

  const goToPage = useCallback(
    (targetPage: number) => {
      if (
        !hasQuery ||
        !pageSize ||
        targetPage < 1 ||
        targetPage > totalPages ||
        targetPage === currentPage ||
        isBusy
      ) {
        return
      }

      const targetPageIndex = targetPage - 1
      const effectiveChunkSize = chunkSize || RESULTS_CHUNK_SIZE
      const targetItemIndex = targetPageIndex * pageSize
      const chunkStart = offset
      const chunkEnd = offset + effectiveChunkSize

      if (targetItemIndex >= chunkStart && targetItemIndex < chunkEnd) {
        dispatch(setPageIndex(targetPageIndex))
        return
      }

      const requestQuery = {
        ...(currentQuery ?? {}),
        offset:
          Math.floor(targetItemIndex / effectiveChunkSize) * effectiveChunkSize,
        max: effectiveChunkSize
      }

      dispatch(setPendingPageIndex(targetPageIndex))
      dispatch(
        getAllResults({
          query: requestQuery,
          meta: { desiredPageIndex: targetPageIndex, replace: true }
        })
      )
    },
    [
      chunkSize,
      currentPage,
      currentQuery,
      dispatch,
      hasQuery,
      isBusy,
      offset,
      pageSize,
      totalPages
    ]
  )

  if (!hasQuery || totalPages === 0) {
    return null
  }

  return (
    <Row className="ResultGridPager mt-3 mb-4">
      <Col className="text-left p-0">
        <Button
          color="primary"
          className="mr-1"
          onClick={() => goToPage(1)}
          disabled={currentPage <= 1 || isBusy}
        >
          <span
            className="glyphicons glyphicons-fast-backward"
            style={{ float: 'left' }}
          ></span>
          First
        </Button>
        <Button
          color="primary"
          className="mr-1"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage <= 1 || isBusy}
        >
          <span
            className="glyphicons glyphicons-rewind"
            style={{ float: 'left' }}
          ></span>
          Prev
        </Button>
      </Col>
      <Col className="text-center">
        <div>{`Page ${currentPage.toLocaleString()} of ${totalPages.toLocaleString()}`}</div>
      </Col>
      <Col className="text-right p-0">
        <Button
          color="primary"
          className="ml-1"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage >= totalPages || isBusy}
        >
          <span
            className="glyphicons glyphicons-forward"
            style={{ float: 'right' }}
          ></span>
          Next
        </Button>
        <Button
          color="primary"
          className="ml-1"
          onClick={() => goToPage(totalPages)}
          disabled={currentPage >= totalPages || isBusy}
        >
          <span
            className="glyphicons glyphicons-fast-forward"
            style={{ float: 'right' }}
          ></span>
          Last
        </Button>
      </Col>
    </Row>
  )
}

export default ResultsPagination
